import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { createLocalStorageTokenStorage } from '@ecommerce/auth-core';

export interface AxiosHttpClientConfig {
  tenantName: string;
  apiProtocol?: string;
  apiDomain?: string;
  apiVersion?: string;
  apiTimeout?: number;
  onLogout?: () => void;
  logoutRedirectUrl?: string;
  userStorage?: { clear: () => void };
}

interface AxiosHttpClient {
  get: <T>(url: string, config?: object) => Promise<T>;
  post: <T>(url: string, data?: any, config?: object) => Promise<T>;
  put: <T>(url: string, data?: any, config?: object) => Promise<T>;
  patch: <T>(url: string, data?: any, config?: object) => Promise<T>;
  delete: <T>(url: string, config?: object) => Promise<T>;
  getWithQueryParamsSerializer: <T>(url: string, params: any, config?: object) => Promise<T>;
  request: <T>(path: string, options?: any) => Promise<T>;
  logout: () => void;
  updateAuthHeader: () => void;
  axiosInstance: AxiosInstance;
  axiosInstanceRefresh: AxiosInstance;
  tokenStorage: ReturnType<typeof createLocalStorageTokenStorage>;
  urlAPI: string;
}

export const createAxiosHttpClient = (config: AxiosHttpClientConfig): AxiosHttpClient => {
  const tokenStorageKey = `${config.tenantName}-tokens`;
  const apiProtocol = config.apiProtocol || (typeof window !== 'undefined' && (window as any).API_PROTOCOL) || 'https';
  const apiDomain = config.apiDomain || (typeof window !== 'undefined' && (window as any).API_DOMAIN) || 'localhost:44381/api';
  const apiVersion = config.apiVersion || (typeof window !== 'undefined' && (window as any).API_VERSION) || 'v1';
  const apiTimeout = config.apiTimeout || (typeof window !== 'undefined' && (window as any).API_TIMEOUT) || 60000;
  const urlAPI = `${apiProtocol}://${apiDomain}/${apiVersion}/`;

  const tokenStorage = createLocalStorageTokenStorage(tokenStorageKey);

  const handleLogout = () => {
    tokenStorage.clear();
    config.userStorage?.clear();

    if (config.onLogout) {
      config.onLogout();
    }

    if (typeof window !== 'undefined') {
      const redirectUrl = config.logoutRedirectUrl || `${window.location.origin}/login`;
      window.location.href = redirectUrl;
    }
  };

  const createAxiosInstance = (): AxiosInstance => {
    const access = tokenStorage.get()?.accessToken;
    
    return axios.create({
      baseURL: urlAPI,
      timeout: apiTimeout,
      headers: {
        'Content-Type': 'application/json',
        ...(access ? { 'Authorization': `Bearer ${access}` } : {}),
      }
    });
  };

  const createAxiosRefreshInstance = (): AxiosInstance => {
    const refresh = tokenStorage.get()?.refreshToken;
    
    return axios.create({
      baseURL: urlAPI,
      timeout: apiTimeout,
      headers: {
        'Content-Type': 'application/json',
        ...(refresh ? { 'Authorization': `Bearer ${refresh}` } : {}),
      }
    });
  };

  let axiosInstance = createAxiosInstance();
  let axiosInstanceRefresh = createAxiosRefreshInstance();

  const updateAuthHeader = () => {
    axiosInstance = createAxiosInstance();
    axiosInstanceRefresh = createAxiosRefreshInstance();
  };

  const tryRefreshToken = async <T>(callback: () => Promise<T>): Promise<T> => {
    try {
      const response = await axiosInstanceRefresh.post("auth/refresh-tokens", {});
      const newAccess = response.data;
      
      if (!newAccess) {
        handleLogout();
        return Promise.reject(new Error('No se pudo refrescar el token'));
      }

      // Actualizar token en storage
      const currentTokens = tokenStorage.get();
      if (currentTokens) {
        tokenStorage.save({
          ...currentTokens,
          accessToken: newAccess
        });
      }

      // Actualizar instancia principal
      updateAuthHeader();

      return callback();
    } catch {
      handleLogout();
      return Promise.reject(new Error('Error al refrescar el token'));
    }
  };

  const handleSuccess = <T>(resp: AxiosResponse<T>): Promise<T> => {
    if (typeof window !== 'undefined' && (window as any).DEBUG_HTTP) {
      console.log(`✅ ${resp.status} ${resp.config.url}`);
    }
    return Promise.resolve(resp.data);
  };

  const handleError = async <T>(
    error: AxiosError,
    retryCallback: () => Promise<T>
  ): Promise<T> => {
    const access = tokenStorage.get()?.accessToken;

    if (error.response?.status === 401) {
      if (!access) {
        return Promise.reject(error);
      }

      return tryRefreshToken(retryCallback);
    }

    let message = 'Ocurrió un error inesperado.';

    if (error.response) {
      if (typeof error.response.data === 'string') {
        message = error.response.data;
      } else if (error.response.data && typeof error.response.data === 'object' && 'message' in error.response.data) {
        message = (error.response.data as any).message;
      } else {
        message = `Error ${error.response.status}.`;
      }
    } else if (error.request) {
      message = 'No se pudo conectar con el servidor.';
    }

    if (typeof window !== 'undefined' && (window as any).DEBUG_HTTP) {
      console.error(`❌ ${error.config?.url}: ${message}`);
    }

    return Promise.reject(new Error(message));
  };

  const get = <T>(url: string, config?: object): Promise<T> =>
    axiosInstance.get<T>(url, config)
      .then(handleSuccess)
      .catch(err => handleError(err, () => get<T>(url, config)));

  const post = <T>(url: string, data?: any, config?: object): Promise<T> =>
    axiosInstance.post<T>(url, data, config)
      .then(handleSuccess)
      .catch(err => handleError(err, () => post<T>(url, data, config)));

  const put = <T>(url: string, data?: any, config?: object): Promise<T> =>
    axiosInstance.put<T>(url, data, config)
      .then(handleSuccess)
      .catch(err => handleError(err, () => put<T>(url, data, config)));

  const patch = <T>(url: string, data?: any, config?: object): Promise<T> =>
    axiosInstance.patch<T>(url, data, config)
      .then(handleSuccess)
      .catch(err => handleError(err, () => patch<T>(url, data, config)));

  const del = <T>(url: string, config?: object): Promise<T> =>
    axiosInstance.delete<T>(url, config)
      .then(handleSuccess)
      .catch(err => handleError(err, () => del<T>(url, config)));

  const getWithQueryParamsSerializer = <T>(url: string, params: any, config?: object): Promise<T> =>
    axiosInstance.get<T>(url, { params, paramsSerializer: { indexes: null }, ...config })
      .then(handleSuccess)
      .catch(err => handleError(err, () => getWithQueryParamsSerializer<T>(url, params, config)));

  /* -------------------------
     MÉTODO REQUEST (Compatibilidad api-core)
  -------------------------- */
  const request = async <T>(path: string, options?: any): Promise<T> => {
    const method = options?.method || 'GET';
    const body = options?.body;
    
    switch (method.toUpperCase()) {
      case 'GET':
        return get<T>(path, options);
      case 'POST':
        return post<T>(path, body, options);
      case 'PUT':
        return put<T>(path, body, options);
      case 'PATCH':
        return patch<T>(path, body, options);
      case 'DELETE':
        return del<T>(path, options);
      default:
        return get<T>(path, options);
    }
  };

  return {
    get,
    post,
    put,
    patch,
    delete: del,
    getWithQueryParamsSerializer,
    request,
    logout: handleLogout,
    updateAuthHeader,
    axiosInstance,
    axiosInstanceRefresh,
    tokenStorage,
    urlAPI,
  };
};
